import { createClient, type SupabaseClient } from '@supabase/supabase-js';
import { env, hasSupabase } from '@/config/env';
import type { Database } from './database.types';

/**
 * Lazy-initialised Supabase client typed against the narrow `Database`
 * shape in database.types.ts.
 *
 * Returns null if env vars are not present so the prototype remains
 * shippable without a backend (callers must handle the null case).
 */

export type HottrSupabaseClient = SupabaseClient<Database>;

let client: HottrSupabaseClient | null = null;

export const getSupabase = (): HottrSupabaseClient | null => {
  if (!hasSupabase()) return null;
  if (client) return client;

  client = createClient<Database>(env.supabaseUrl, env.supabaseAnonKey, {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      // OFF: Passport SSO also lands on /auth/callback?code=… and Supabase
      // would try to consume that one-time code as its own PKCE exchange,
      // racing the Passport handoff and hanging the "Holding the door" screen.
      // AuthCallbackPage mints the destination session via verifyOtp explicitly.
      detectSessionInUrl: false,
      flowType: 'pkce',
      storageKey: 'hottr.app.auth',
    },
  });

  return client;
};

export const isSupabaseReady = (): boolean => hasSupabase();
